import React, { useState, useEffect, useContext } from "react";
import {
  Row,
  Col,
  Card,
  message,
  Divider,
  Space,
  Button,
  Modal,
  Typography,
  Tag,
  Form,
  Input,
  Checkbox,
  InputNumber,
} from "antd";
import { ExceptionOutlined, LockOutlined, EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import { HeaderPage } from "../../../components/headerPage";
import { HeaderTitle } from "../../../components/headerTitle";
import { useNavigate, useParams } from "react-router-dom";
import { RenderPdf } from "../../../components/renderPdf";
import http from "../../../services/http";
import dayjs from "dayjs";
import { AuthContext } from "../../../contexts/AuthContext";
import { listarPendencias } from "../../../helpers/pendencias";
import { parseToInt, randomInt } from "../../../helpers/masks";

const validator = {
  confirmacao: [
    {
      validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error("Marque esta opção!"))),
    },
  ],
  operacao_matematica: [{ required: true, message: "Digite a resposta!" }],
};

const { Text } = Typography;
export const AvisoFeriasDetalhes = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [firstNumber] = useState(randomInt(9));
  const [secondNumber] = useState(randomInt(9));
  const { id_aviso_ferias } = useParams();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState({});
  const [modal, contextHolder] = Modal.useModal();
  const [arquivo, setArquivo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { atualizarPendencias } = useContext(AuthContext);

  useEffect(() => {
    if (id_aviso_ferias) {
      carregarAvisosFerias(id_aviso_ferias);
      carregarAvisosFeriasDocumento(id_aviso_ferias);
    }
  }, [id_aviso_ferias]);

  // LISTAR
  const carregarAvisosFerias = async (id) => {
    if (!id) return;
    setLoading(true);
    const api = await http("get", `v1/aviso_ferias/carregar/${id}`, []);
    if (api.statusCode === 200) {
      if (!api.response.data) {
        navigate("/aviso-ferias");
      }

      if (api?.response?.data?.status_confirmacao === 0) {
        modal.info({
          title: "Atenção",
          content: "Verifique se os dados presentes estão corretos e confirme o recebimento do documento.",
          onOk() {},
        });
      }
      setDados(api.response.data);
    } else {
      navigate("/aviso-ferias");
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // CARREGAR DOCUMENTO
  const carregarAvisosFeriasDocumento = async (id) => {
    if (!id) return;
    setLoading(true);
    const api = await http("file", `v1/aviso_ferias/carregar_documento/${id}`, []);
    if (api.statusCode === 200) {
      if (!api.response.data) {
        navigate("/aviso-ferias");
      }

      // CRIAR ARQUIVO PDF
      var blob = new Blob([api.response.data], {
        type: "application/pdf",
      });

      setArquivo(window.URL.createObjectURL(blob));
    } else {
      navigate("/aviso-ferias");
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // CONFIRMAR
  const confirmarAvisoFerias = async (formulario) => {
    if (dados.status_confirmacao === 1) {
      message.error("Documento já confirmado!");
      setShowModal(false);
      form.resetFields();
      return;
    }
    setLoading(true);
    const api = await http("post", `v2/aviso_ferias/confirmar`, formulario);
    if (api.statusCode === 200) {
      setShowModal(false);
      form.resetFields();
      carregarAvisosFerias(id_aviso_ferias);
      message.success("Aviso de férias confirmado com sucesso!");

      // ATUALIZAR PENDÊNCIAS
      listarPendencias(atualizarPendencias);
    } else {
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // SUBMIT FORM
  const onSubmit = (formulario) => {
    if (firstNumber + secondNumber !== parseToInt(formulario?.operacao_matematica)) {
      form.setFields([
        {
          name: "operacao_matematica",
          errors: ["Resultado incorreto!"],
        },
      ]);
      return;
    }

    confirmarAvisoFerias({
      id: id_aviso_ferias,
      operacao_matematica: `${firstNumber} + ${secondNumber} = ${parseToInt(formulario?.operacao_matematica)}`,
      senha: formulario?.senha,
    });
  };

  // FORMULÁRIO
  const modalFormulario = (
    <Modal
      title={"Formulário de Aceite de Documento"}
      open={showModal}
      onOk={() => form.submit()}
      okText="Salvar"
      width={600}
      okButtonProps={{ size: "large", loading: loading }}
      cancelButtonProps={{ size: "large", loading: loading }}
      onCancel={() => {
        setShowModal(false);
        form.resetFields();
      }}>
      <Form form={form} onFinish={onSubmit} layout="vertical" autoComplete="off">
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col className="gutter-row" span={24}>
            <Form.Item name="confirmacao" valuePropName="checked" rules={validator?.confirmacao}>
              <Checkbox disabled={loading}>
                Declaro que li e concordo com as informações contidas no documento.
              </Checkbox>
            </Form.Item>
          </Col>

          <Col className="gutter-row" span={24}>
            <Form.Item
              name="operacao_matematica"
              label="Resolva a Operação Matemática"
              rules={validator?.operacao_matematica}>
              <Space>
                <Text style={{ fontSize: 20 }}>
                  {firstNumber} + {secondNumber} ={" "}
                </Text>
                <InputNumber
                  controls={false}
                  autoComplete="off"
                  className="input-number"
                  precision={0}
                  min={0}
                  style={{ width: 100 }}
                  size="large"
                  disabled={loading}
                  placeholder="..."
                />
              </Space>
            </Form.Item>
          </Col>

          <Col className="gutter-row" span={24}>
            <Form.Item
              className="FormItem"
              label="Senha de acesso do portal do colaborador"
              name="senha"
              rules={[{ required: true, message: "Digite sua senha!" }]}>
              <Input.Password
                size="large"
                autoComplete="off"
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Digite sua senha de uso pessoal e instranferível"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );

  return (
    <Row align="middle" justify="center" className="content-row">
      {modalFormulario}
      <Col span={24} align="middle" justify="center">
        <Col xs={24} sm={24} md={22} lg={22} xl={20} xxl={16} align="left">
          <div className="content-item">
            <HeaderPage title="Documentos" onClickBack={() => navigate("/aviso-ferias")} />
            <HeaderTitle
              title="Aviso de Férias"
              loading={loading}
              subtitle={dayjs(dados?.prgdat).format("DD/MM/YYYY")}
              icon={<ExceptionOutlined />}
            />
            <Divider />
            <Card bordered={false} bodyStyle={{ padding: "3%" }}>
              <Space wrap size={20}>
                {dados?.status_confirmacao === 0 && (
                  <Button onClick={() => setShowModal(true)} size="large" type="primary">
                    Confirmar Visualização
                  </Button>
                )}

                <Space direction="vertical" size={0}>
                  <Text style={{ fontSize: 12 }} type="secondary">
                    Data:
                  </Text>
                  <Text style={{ fontSize: 14 }} strong>
                    {dayjs(dados?.prgdat).format("DD/MM/YYYY")}
                  </Text>
                </Space>
                <Space direction="vertical" size={0}>
                  <Text style={{ fontSize: 12 }} type="secondary">
                    Status Confirmação:
                  </Text>
                  <Text style={{ fontSize: 14 }} strong>
                    {dados?.status_confirmacao === 1 ? (
                      <Tag color="green">Confirmado</Tag>
                    ) : (
                      <Tag color="red">Pendente</Tag>
                    )}
                  </Text>
                </Space>
              </Space>

              <Divider />
              <RenderPdf file={arquivo} />
            </Card>
          </div>
        </Col>
      </Col>
      {contextHolder}
    </Row>
  );
};
